import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../api/axiosInstance';
import { FaTrash } from 'react-icons/fa';
import colors from '../../constants/colors';

const ServiceForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        short_description: '',
        schedule_link: '',
        image: null,
        order: 0
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [validated, setValidated] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);

    useEffect(() => {
        if (!currentUser || !currentUser.isAdmin) {
            navigate('/');
            return;
        }

        if (isEditMode) {
            const fetchService = async () => {
                try {
                    setLoading(true);
                    const response = await axiosInstance.get(`/service/${id}`);
                    
                    if (response) {
                        setFormData({
                            ...response,
                            image: null,
                            order: response.order || 0
                        });

                        if (response.image) {
                            setPreviewImage(response.image);
                        }
                    }
                } catch (error) {
                    console.error('Error fetching service:', error);
                    setError('Erro ao carregar o serviço. Por favor, tente novamente.');
                } finally {
                    setLoading(false);
                }
            };
            fetchService();
        }
    }, [currentUser, id, isEditMode, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                image: file
            }));
            setPreviewImage(URL.createObjectURL(file));
        }
    };

    const handleDeleteImage = () => {
        setFormData(prev => ({
            ...prev,
            image: null
        }));
        setPreviewImage(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        
        if (form.checkValidity() === false) {
            e.stopPropagation();
            setValidated(true);
            return;
        }

        try {
            setLoading(true);
            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.name);
            formDataToSend.append('description', formData.description);
            formDataToSend.append('short_description', formData.short_description);
            formDataToSend.append('schedule_link', formData.schedule_link);
            formDataToSend.append('order', formData.order);
            
            if (formData.image) {
                formDataToSend.append('image', formData.image);
            }

            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            };

            let response;
            if (isEditMode) {
                response = await axiosInstance.put(`/service/${id}`, formDataToSend, config);
            } else {
                response = await axiosInstance.post('/service', formDataToSend, config);
            }

            if (response.status === 200 || response.status === 201) {
                navigate('/admin-dashboard');
            }
        } catch (error) {
            console.error('Error submitting service:', error);
            setError('Erro ao salvar o serviço. Por favor, tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteService = async () => {
        if (!window.confirm('Tem certeza que deseja excluir este serviço?')) {
            return;
        }

        try {
            setLoading(true);
            await axiosInstance.delete(`/service/${id}`);
            navigate('/admin-dashboard');
        } catch (error) {
            setError('Erro ao excluir o serviço');
            console.error('Error deleting service:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container className="py-4">
            <Card className="shadow-sm">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">{isEditMode ? 'Editar Serviço' : 'Criar Serviço'}</h5>
                    {isEditMode && (
                        <Button
                            variant="danger"
                            size="sm"
                            onClick={handleDeleteService}
                            disabled={loading}
                            style={{ backgroundColor: colors.secondary, borderColor: colors.secondary }}
                        >
                            <FaTrash className="me-2" />
                            {loading ? 'A eliminar...' : 'Eliminar'}
                        </Button>
                    )}
                </Card.Header>
                <Card.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    {loading ? (
                        <div className="text-center my-4">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">A carregar...</span>
                            </Spinner>
                        </div>
                    ) : (
                        <Form noValidate validated={validated} onSubmit={handleSubmit}>
                            <Form.Group className="mb-3">
                                <Form.Label>Nome</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    required
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Nome do serviço"
                                />
                                <Form.Control.Feedback type="invalid">
                                    Por favor, insira um nome para o serviço.
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Descrição</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    name="description"
                                    required
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder="Descrição do serviço"
                                />
                                <Form.Control.Feedback type="invalid">
                                    Por favor, insira uma descrição para o serviço.
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Descrição curta</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    name="short_description"
                                    value={formData.short_description}
                                    onChange={handleChange}
                                    placeholder="Descrição curta do serviço"
                                />
                                <Form.Control.Feedback type="invalid">
                                    Por favor, insira uma descrição curta para o serviço.
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Ordem</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="order"
                                    value={formData.order}
                                    onChange={handleChange}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">Por favor insira a ordem.</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Link do Calendário</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="schedule_link"
                                    required
                                    value={formData.schedule_link}
                                    onChange={handleChange}
                                    placeholder="Link do calendário"
                                />
                                <Form.Control.Feedback type="invalid">
                                    Por favor, insira um link para o calendário.
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Imagem</Form.Label>
                                <Form.Control
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                />
                                {(previewImage || formData.image) && (
                                    <div className="mt-2 d-flex flex-column align-items-start">
                                        <img
                                            src={previewImage || (formData.image ? URL.createObjectURL(formData.image) : '')}
                                            alt="Imagem do serviço"
                                            className="img-fluid mb-2"
                                            style={{ maxHeight: '200px' }}
                                        />
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={handleDeleteImage}
                                            className="mt-2"
                                            disabled={loading}
                                        >
                                            <FaTrash className="me-2" />
                                            Remover Imagem
                                        </Button>
                                    </div>
                                )}
                            </Form.Group>
                            <Button
                                type="submit"
                                disabled={loading}
                                style={{ backgroundColor: colors.secondary, borderColor: colors.secondary }}
                            >
                                {loading ? 'A guardar...' : 'Guardar'}
                            </Button>
                        </Form>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
};

export default ServiceForm;
